import React, { useState, useEffect } from 'react';
import { X, Hammer } from 'lucide-react';
import { crewService } from '../../services/crewService';
import { subcontractorService } from '../../services/subcontractorService';

/**
 * Unified picker for assigning Crews and/or Subcontractors.
 * Crews and subs are kept as separate ID arrays (DB-side); the UI groups them visually.
 *
 * Props:
 *  - selectedCrewIds: string[]
 *  - selectedSubcontractorIds: string[]
 *  - onChange: ({ crewIds, subcontractorIds }) => void
 *  - disabled?: boolean
 *  - label?: string
 *  - placeholder?: string
 *  - renderCrewExtra?: (crew) => ReactNode  // optional render slot under each selected crew chip (e.g. hours input)
 */
const TeamResourceMultiSelect = ({
  selectedCrewIds = [],
  selectedSubcontractorIds = [],
  onChange,
  disabled = false,
  label = "Assigned Team Resources",
  placeholder = "Select crews or subcontractors...",
  renderCrewExtra = null,
}) => {
  const [crews, setCrews] = useState([]);
  const [subs, setSubs] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [cRes, sRes] = await Promise.all([
          crewService.getAll(),
          subcontractorService.getAll(),
        ]);
        if (!mounted) return;
        const cData = cRes?.data || cRes || [];
        const sData = sRes?.data || sRes || [];
        setCrews((Array.isArray(cData) ? cData : []).filter(c => c.is_active !== false));
        setSubs((Array.isArray(sData) ? sData : []).filter(s => s.status !== 'inactive'));
      } catch (err) {
        console.error('TeamResourceMultiSelect fetch failed:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const idOf = (item) => item._id || item.id;

  const toggleCrew = (crewId) => {
    if (disabled) return;
    const next = selectedCrewIds.includes(crewId)
      ? selectedCrewIds.filter(id => id !== crewId)
      : [...selectedCrewIds, crewId];
    onChange({ crewIds: next, subcontractorIds: selectedSubcontractorIds });
  };

  const toggleSub = (subId) => {
    if (disabled) return;
    const next = selectedSubcontractorIds.includes(subId)
      ? selectedSubcontractorIds.filter(id => id !== subId)
      : [...selectedSubcontractorIds, subId];
    onChange({ crewIds: selectedCrewIds, subcontractorIds: next });
  };

  const removeCrew = (crewId, e) => {
    e?.stopPropagation();
    if (disabled) return;
    onChange({
      crewIds: selectedCrewIds.filter(id => id !== crewId),
      subcontractorIds: selectedSubcontractorIds,
    });
  };

  const removeSub = (subId, e) => {
    e?.stopPropagation();
    if (disabled) return;
    onChange({
      crewIds: selectedCrewIds,
      subcontractorIds: selectedSubcontractorIds.filter(id => id !== subId),
    });
  };

  const selectedCrews = crews.filter(c => selectedCrewIds.includes(idOf(c)));
  const selectedSubs = subs.filter(s => selectedSubcontractorIds.includes(idOf(s)));
  const totalSelected = selectedCrews.length + selectedSubs.length;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      {/* Selected chips */}
      {(selectedCrews.length > 0 || selectedSubs.length > 0) && (
        <div className="space-y-2 mb-2">
          {selectedCrews.map((crew) => (
            <div key={`crew-${idOf(crew)}`} className="space-y-1">
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                style={{
                  backgroundColor: crew.color ? `${crew.color}20` : '#dbeafe',
                  color: crew.color || '#1e40af',
                }}
              >
                <span className="w-2 h-2 rounded-full bg-current opacity-60" />
                {crew.name}
                <span className="text-xs opacity-70">(crew)</span>
                {!disabled && (
                  <button type="button" onClick={(e) => removeCrew(idOf(crew), e)} className="hover:bg-black/10 rounded-full p-0.5 ml-1">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
              {renderCrewExtra && renderCrewExtra(crew)}
            </div>
          ))}
          {selectedSubs.map((sub) => (
            <span
              key={`sub-${idOf(sub)}`}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-900 mr-2"
            >
              {sub.name}
              <span className="text-xs opacity-70">(sub{sub.specialty ? ` • ${sub.specialty}` : ''})</span>
              {!disabled && (
                <button type="button" onClick={(e) => removeSub(idOf(sub), e)} className="hover:bg-black/10 rounded-full p-0.5 ml-1">
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md text-left flex items-center justify-between ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
          }`}
        >
          <span className="flex items-center gap-2 text-gray-600">
            <Hammer className="w-4 h-4" />
            {totalSelected > 0 ? `${totalSelected} selected` : placeholder}
          </span>
          <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && !disabled && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-auto">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
              ) : (
                <>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">CREWS</div>
                  {crews.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No active crews. Add one in Settings &gt; Crews.</div>
                  ) : (
                    crews.map((crew) => {
                      const cId = idOf(crew);
                      const isSelected = selectedCrewIds.includes(cId);
                      return (
                        <button
                          key={`crew-opt-${cId}`}
                          type="button"
                          onClick={() => toggleCrew(cId)}
                          className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${isSelected ? 'bg-blue-50' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            {crew.color && <span className="w-3 h-3 rounded-full" style={{ backgroundColor: crew.color }} />}
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900">{crew.name}</span>
                              <span className="text-xs text-gray-500">
                                {(crew.members?.length || 0)} member(s){crew.is_hourly === false ? ' • Non-Hourly' : ''}
                              </span>
                            </div>
                          </div>
                          {isSelected && (
                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      );
                    })
                  )}

                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0 mt-1">SUBCONTRACTORS</div>
                  {subs.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No subcontractors found.</div>
                  ) : (
                    subs.map((sub) => {
                      const sId = idOf(sub);
                      const isSelected = selectedSubcontractorIds.includes(sId);
                      return (
                        <button
                          key={`sub-opt-${sId}`}
                          type="button"
                          onClick={() => toggleSub(sId)}
                          className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${isSelected ? 'bg-amber-50' : ''}`}
                        >
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">{sub.name}</span>
                            <span className="text-xs text-gray-500">{sub.specialty || 'Subcontractor'}</span>
                          </div>
                          {isSelected && (
                            <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeamResourceMultiSelect;
